import { ReactNode } from 'react';

type MenuItemWithComponent = {
  component: ReactNode;
  onClick(): void;
  disabled?: boolean;
  withDivider?: boolean;
  inline?: boolean;
  'data-testid'?: string;
};

export type MenuItemWithText = {
  text: string;
  /**
   * A second, de-emphasised line under the row's text.
   *
   * A slot of its own rather than part of a `component`, which is what it used to be: the join
   * choice built a `Stack` with a label and a caption inside it and passed the whole thing as the
   * row. That made the row a `MenuItemWithComponent`, which `toDsProps` REFUSES — so the one menu
   * in the app with described rows was also the one menu that could never refresh.
   *
   * Split out, both paths render it: MUI as a second `Typography` inside the row, DS through
   * `MenuOption.description`.
   */
  description?: string;
  color?: ({} & string) | 'danger';
  icon?: ReactNode;
  onClick?(): void;
  disabled?: boolean;
  direction?: 'left' | 'right';
  severity?: 'default' | 'danger';
  withDivider?: boolean;
  /** Whether the menu item is displayed inline next to the more actions button */
  inline?: boolean;
  'data-testid'?: string;
};

export type NestedMenuItemWithText = MenuItemWithText & {
  nestedItems: MenuItemType[];
};

export type MenuItemType = MenuItemWithComponent | MenuItemWithText | NestedMenuItemWithText;

export const isMenuItemWithText = (item: MenuItemType): item is MenuItemWithText =>
  (item as MenuItemWithText).text !== undefined;

export const isMenuItemWithComponent = (item: MenuItemType): item is MenuItemWithComponent =>
  (item as MenuItemWithComponent).component !== undefined;

export const isNestedMenuItemWithText = (item: MenuItemType): item is NestedMenuItemWithText =>
  isMenuItemWithText(item) && (item as NestedMenuItemWithText).nestedItems !== undefined;
